import { CircularProgress } from '@renderer-ui-library/mui'
import { useHeaderHeight } from '@renderer-ui-library/scaffolding/Header/useHeaderHeight'
import classNames from 'classnames'
import { FC, useEffect, useMemo, useRef, useState } from 'react'
import styles from './loaderOverlay.module.scss'

type Props = {
  show: boolean
  className?: string
}

const getViewportHeight = () =>
  Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0)

const loaderHeight = 40
const spacing = 40 + loaderHeight / 2

export const LoaderOverlay: FC<Props> = (props) => {
  const overlayRef = useRef<HTMLDivElement>(null)
  const [viewportHeight, setViewPortHeight] = useState(0)
  const [overlayRect, setOverlayRect] = useState<DOMRect | null>(null)
  const headerHeight = useHeaderHeight()

  const offset = useMemo(() => {
    if (!overlayRect) {
      return 0
    }
    const visibleAreaTop = Math.max(0, -(overlayRect.top - headerHeight))
    const visibleAreaBottom = Math.min(
      overlayRect.height,
      visibleAreaTop +
        (viewportHeight - headerHeight) -
        Math.max(0, overlayRect.y - headerHeight)
    )
    const visibleArea = visibleAreaBottom - visibleAreaTop
    let centerPosition = visibleAreaTop + visibleArea / 2

    // ensure loader keeps bottom spacing
    centerPosition = Math.min(centerPosition, overlayRect.height - spacing)
    // ensure loader keeps top spacing
    centerPosition = Math.max(centerPosition, spacing)

    return centerPosition
  }, [viewportHeight, overlayRect, headerHeight])

  useEffect(() => {
    if (!props.show) {
      return
    }

    setViewPortHeight(getViewportHeight())
    const onScroll = () =>
      setOverlayRect(overlayRef.current!.getBoundingClientRect())

    const onWindowResize = () => {
      setViewPortHeight(getViewportHeight())
      setOverlayRect(overlayRef.current!.getBoundingClientRect())
    }
    const resizeObserver = new ResizeObserver(() =>
      setOverlayRect(overlayRef.current!.getBoundingClientRect())
    )

    if (overlayRef.current) {
      setOverlayRect(overlayRef.current!.getBoundingClientRect()),
        resizeObserver.observe(overlayRef.current)
    }

    document.addEventListener('scroll', onScroll)
    window.addEventListener('resize', onWindowResize)

    return () => {
      document.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onWindowResize)
      resizeObserver.disconnect()
    }
  }, [props.show])

  return (
    <div
      className={classNames(props.className, styles.overlay, {
        [styles.show]: props.show,
      })}
      ref={overlayRef}
    >
      <div
        style={{
          transform: props.show ? `translateY(${offset}px)` : undefined,
        }}
        className={styles.loader}
      >
        <CircularProgress variant='indeterminate' color='primary' />
      </div>
    </div>
  )
}
